/**
 * Sub-Tab Access Registry — Access control for tabs WITHIN pages
 *
 * While TAB_ACCESS_REGISTRY (tabAccessRegistry.ts) controls which top-level
 * pages/tabs a user can access, this registry controls which sub-tabs
 * within those pages are visible based on role.
 *
 * Structure: { [pageKey]: { [tabKey]: SubTabRule } }
 *
 * To add a new in-page tab:
 *   1. Add the page key if it doesn't exist
 *   2. Add the tab key with roles/capability
 *   3. Wrap the tab content with <GatedTabContent pageKey="..." tabKey="...">
 */

export interface SubTabRule {
  roles?: string[];
  capability?: string;
  public?: boolean;
  sensitive?: boolean; // Flags tabs that contain PII/PHI/financial data
  description?: string;
}

// These must match CBORoleType values from types/cboRoles.ts
// API roles are mapped: ops_admin → program_manager, manager → supervisor
// Exported so a tab that gates a SECTION inside itself (see
// RecordsAndCertificationsPage) re-checks the same list rather than
// restating it — a second copy drifts, and this one decides who sees
// surfaces the backend 403s on.
export const ADMIN_ROLES = ["super_admin", "org_admin", "program_manager"];
const MANAGER_ROLES = [...ADMIN_ROLES, "supervisor"];
const STAFF_ROLES = [...MANAGER_ROLES, "staff"];

/**
 * Registry of sub-tab access rules, keyed by page then tab.
 */
export const SUB_TAB_REGISTRY: Record<string, Record<string, SubTabRule>> = {

  // ── LCC Training & Workforce hubs (lms-gated) ──────────────────
  // Integrated pages that fold the individual LMS / workforce items into two
  // internal-tabbed hubs. Role rules mirror the standalone tabs they embed.
  "workforce-learning-hub": {
    "my-learning":    { roles: [...STAFF_ROLES], capability: "lms", description: "Staff learner: take classroom courses" },
    "courses":        { roles: [...ADMIN_ROLES], capability: "lms", description: "Author classroom LMS courses" },
    "curricula":      { roles: [...MANAGER_ROLES], capability: "workforce_development", description: "Training curricula" },
    // certifications intentionally absent — they live in the Training hub only
    "my-trainings":   { roles: [...STAFF_ROLES], capability: "staff_training", description: "Staff: my training requirements and completions (TRUS-486)" },
    // TRUS-562. STAFF_ROLES rather than MANAGER_ROLES on purpose: whether you
    // see anything here is decided by whether anyone REPORTS to you, which the
    // server derives from the reporting line — not by your role. A supervisor
    // with no reports and a staff member with three should each get the honest
    // answer, and gating on role would give the wrong one to both.
    "my-team":        { roles: [...STAFF_ROLES], capability: "staff_training", sensitive: true, description: "Manager: training owed by my direct reports (TRUS-562)" },
  },
  "workforce-training-hub": {
    "cohorts":        { roles: [...MANAGER_ROLES], capability: "lms", description: "Member cohorts: candidate → selected → enrolled funnel" },
    "schedule":       { roles: [...MANAGER_ROLES], capability: "lms", description: "Training schedule, RSVP & attendance" },
    // TRUS-562: "Records and Certifications should be one tab and managed based
    // on the type of training/learning scenarios." They are two halves of the
    // same question — what has this person completed, and what are they
    // credentialed for — split only by where the training happened.
    //
    // Gated on workforce_development and MANAGER_ROLES, which is the WIDER of
    // the two rules it replaces, because the tab now has to appear for anyone
    // who could see EITHER half. LCC holds workforce_development but NOT
    // staff_training, so gating on staff_training would have taken their
    // Certifications away — and in-app certification is the half LCC actually
    // uses. The records half is gated again INSIDE the tab, on staff_training
    // plus admin, so nobody gains access to a surface the backend would 403.
    "records-certifications": { roles: [...MANAGER_ROLES], capability: "workforce_development", sensitive: true, description: "Training completions and issued certifications" },
    // Staff training program (TRUS-486). ADMIN_ROLES throughout because the backend
    // itself is admin-only on these surfaces: /training-assignments/summary and
    // /training-reports/* return 403 to anyone else, and a non-admin listing records sees
    // only their own — which is what the "My Trainings" tab in the Learning hub is for.
    // Showing a tab that 403s would be a bug, not a permission.
    "catalogue":      { roles: [...ADMIN_ROLES], capability: "staff_training", description: "Define trainings, competencies and due-date rules" },
    "compliance":     { roles: [...ADMIN_ROLES], capability: "staff_training", sensitive: true, description: "Who is overdue, due soon, or unevaluated" },
    "reports":        { roles: [...ADMIN_ROLES], capability: "staff_training", sensitive: true, description: "Searchable training report and export" },
  },

  // ── Member Profile ─────────────────────────────────────────────
  "member-profile": {
    "demographics":    { roles: [...STAFF_ROLES], sensitive: true, description: "PII: name, DOB, SSN, contact info" },
    "referrals":       { roles: [...STAFF_ROLES], description: "Member referral history" },
    "enrollments":     { roles: [...STAFF_ROLES], description: "Program enrollments" },
    "conflicts":       { roles: [...MANAGER_ROLES], description: "Data conflict resolution" },
    "health-plan":     { roles: [...MANAGER_ROLES], sensitive: true, description: "Insurance/health plan coverage" },
    "family":          { roles: [...STAFF_ROLES], sensitive: true, description: "Family member details" },
    "caregivers":      { roles: [...MANAGER_ROLES], sensitive: true, description: "Caregiver authorization & permissions" },
    "ai-enrichment":   { roles: [...ADMIN_ROLES], description: "AI data enrichment results" },
    "consent":         { roles: [...MANAGER_ROLES], sensitive: true, description: "HIPAA/medical consent records" },
    "work-compliance": { roles: [...MANAGER_ROLES], sensitive: true, description: "Medicaid work compliance" },
    "workforce":       { roles: [...MANAGER_ROLES], capability: "workforce_development", description: "Employment & workforce data" },
  },

  // ── Health Plan Detail ─────────────────────────────────────────
  "health-plan-detail": {
    "lobs":      { roles: [...ADMIN_ROLES], description: "Lines of business" },
    "products":  { roles: [...ADMIN_ROLES], description: "Plan products" },
    "networks":  { roles: [...ADMIN_ROLES], description: "Provider networks" },
    "pharmacy":  { roles: [...ADMIN_ROLES], sensitive: true, description: "Pharmacy benefits configuration" },
    "employers": { roles: [...ADMIN_ROLES], sensitive: true, description: "Employer contract terms & rates" },
    "cbos":      { roles: [...ADMIN_ROLES], sensitive: true, description: "CBO partnership agreements & revenue sharing" },
  },

  // ── Billing Activities ─────────────────────────────────────────
  "billing-activities": {
    "activities":   { roles: [...ADMIN_ROLES], sensitive: true, description: "Billable activities & rates" },
    "diagnosis":    { roles: [...ADMIN_ROLES], description: "ICD-10 diagnosis code mapping" },
    "pos":          { roles: [...ADMIN_ROLES], description: "Place of service codes" },
    "dependencies": { roles: [...ADMIN_ROLES], description: "Activity bundling rules" },
  },

  // ── Document Processing ────────────────────────────────────────
  "document-processing": {
    "member-profiles":  { roles: [...ADMIN_ROLES], sensitive: true, description: "Insurance card OCR & member matching" },
    "master-claims":    { roles: [...ADMIN_ROLES], sensitive: true, description: "Per-member claim submission" },
    "full-claim-entry": { roles: [...ADMIN_ROLES], sensitive: true, description: "Full claim OCR extraction" },
  },

  // ── Developer Portal ───────────────────────────────────────────
  "developer-portal": {
    "overview": { roles: [...ADMIN_ROLES], description: "Developer portal overview" },
    "docs":     { roles: [...ADMIN_ROLES], description: "API documentation" },
    // The page renders this tab; without an entry here canAccessSubTab blocks it
    // by default, so "API Guide" was unreachable — the registry's fail-closed
    // rule is right, the entry was simply never added with the tab.
    "api-guide": { roles: [...ADMIN_ROLES], description: "API workflows and endpoint summary (GET /api/api-guide/*)" },
    "sandbox":  { roles: [...ADMIN_ROLES], sensitive: true, description: "Sandbox provisioning & API keys" },
    "usage":    { roles: [...ADMIN_ROLES], description: "API usage analytics" },
  },

  // ── RAG Admin ──────────────────────────────────────────────────
  "rag-admin": {
    "documents":  { roles: [...ADMIN_ROLES], description: "Document corpus management" },
    "ingestion":  { roles: [...ADMIN_ROLES], description: "Ingestion job monitoring" },
    "config":     { roles: ["super_admin", "program_manager"], description: "RAG feature configuration" },
    "privacy":    { roles: ["super_admin", "program_manager"], sensitive: true, description: "PHI detection audit log" },
  },

  // ── User Management ────────────────────────────────────────────
  "user-management": {
    "users":       { roles: [...ADMIN_ROLES], description: "Staff user accounts" },
    "portal":      { roles: [...ADMIN_ROLES], description: "Portal member accounts" },
    "invitations": { roles: [...ADMIN_ROLES], description: "Pending invitations" },
  },

  // ── Enrollment Detail ──────────────────────────────────────────
  "enrollment-detail": {
    "overview":      { roles: [...STAFF_ROLES], description: "Enrollment overview" },
    "activities":    { roles: [...STAFF_ROLES], description: "Outreach & follow-up history" },
    "documents":     { roles: [...STAFF_ROLES], sensitive: true, description: "Enrollment documents & consent" },
    "notes":         { roles: [...MANAGER_ROLES], sensitive: true, description: "Internal case notes" },
    "goals":         { roles: [...STAFF_ROLES], description: "Health/service goals" },
    "tasks":         { roles: [...STAFF_ROLES], description: "Actionable tasks" },
    "conversations": { roles: [...STAFF_ROLES], description: "Member communications" },
    "forms":         { roles: [...STAFF_ROLES], description: "Form responses" },
    "workflow":      { roles: [...STAFF_ROLES], description: "Workflow instance tracking" },
    "signatures":    { roles: [...MANAGER_ROLES], description: "Signature requests" },
  },

  // ── Workforce Case Detail ──────────────────────────────────────
  "workforce-case": {
    "overview":       { roles: [...MANAGER_ROLES], description: "Case summary" },
    "activities":     { roles: [...MANAGER_ROLES], description: "Case history" },
    "documents":      { roles: [...MANAGER_ROLES], sensitive: true, description: "Case documents" },
    "notes":          { roles: [...MANAGER_ROLES], sensitive: true, description: "Internal case notes" },
    "goals":          { roles: [...MANAGER_ROLES], description: "Case goals" },
    "tracking":       { roles: [...MANAGER_ROLES], description: "Timeline events" },
    "communications": { roles: [...MANAGER_ROLES], description: "Correspondence" },
    "assessments":    { roles: [...MANAGER_ROLES], sensitive: true, description: "Assessment results" },
  },

  // ── Employee Benefits Portal ───────────────────────────────────
  "employee-benefits": {
    "programs":   { public: true, description: "Benefit program enrollment" },
    "claims":     { public: true, sensitive: true, description: "Claims & EOBs" },
    "accounts":   { public: true, sensitive: true, description: "HSA/FSA balances" },
    "id-cards":   { public: true, sensitive: true, description: "Insurance card details" },
    "dependents": { public: true, sensitive: true, description: "Dependent information" },
    "providers":  { public: true, description: "Provider directory" },
  },

  // ── Phone Number Management ────────────────────────────────────
  "phone-management": {
    "overview":    { roles: [...ADMIN_ROLES], description: "Number usage summary" },
    "provision":   { roles: ["super_admin", "program_manager"], description: "Provision new numbers" },
    "user-assign": { roles: [...ADMIN_ROLES], description: "Assign numbers to staff" },
    "routing":     { roles: [...ADMIN_ROLES], description: "Campaign/program routing" },
  },

  // ── Integration Dashboard ──────────────────────────────────────
  "integrations": {
    "executions": { roles: [...ADMIN_ROLES], description: "Job execution history" },
    "health":     { roles: [...ADMIN_ROLES], description: "Integration health metrics" },
    "logs":       { roles: [...ADMIN_ROLES], sensitive: true, description: "Execution logs" },
  },

  // ── Employer Settings ──────────────────────────────────────────
  "employer-settings": {
    "capabilities": { roles: [...ADMIN_ROLES], description: "Feature flags per tier" },
    "account":      { roles: [...ADMIN_ROLES], description: "Account configuration" },
  },

  // ── Employer Benefits Dashboard ────────────────────────────────
  "employer-benefits-dashboard": {
    "plans":       { roles: [...ADMIN_ROLES], description: "Plan details & rates" },
    "enrollments": { roles: [...ADMIN_ROLES], description: "Employee enrollment stats" },
    "claims":      { roles: [...ADMIN_ROLES], sensitive: true, description: "Claims summary by plan" },
    "pharmacy":    { roles: [...ADMIN_ROLES], description: "Pharmacy benefit management" },
  },
};


/**
 * Check if a user role has access to a specific sub-tab.
 */
export function checkSubTabAccess(
  pageKey: string,
  tabKey: string,
  userRole: string | undefined | null,
): { allowed: boolean; reason?: string } {
  const page = SUB_TAB_REGISTRY[pageKey];
  if (!page) {
    // Page not in registry — allow by default (only registered pages are enforced)
    return { allowed: true };
  }

  const rule = page[tabKey];
  if (!rule) {
    // Tab not in registry for this page — block by default
    return {
      allowed: false,
      reason: `Tab "${tabKey}" in page "${pageKey}" is not registered in SUB_TAB_REGISTRY.`,
    };
  }

  if (rule.public) {
    return { allowed: true };
  }

  if (!userRole) {
    return { allowed: false, reason: "No user role available" };
  }

  if (rule.roles && rule.roles.length > 0) {
    if (!rule.roles.includes(userRole)) {
      return {
        allowed: false,
        reason: `Your role does not have access to this section.`,
      };
    }
  }

  return { allowed: true };
}

/**
 * Get all registered sub-tab keys for a page.
 */
export function getRegisteredSubTabs(pageKey: string): string[] {
  const page = SUB_TAB_REGISTRY[pageKey];
  return page ? Object.keys(page) : [];
}
